import { useEffect, useState } from 'react';

/**
 * REV20 — a HEXA node type's generic icon, sourced from `frontend/public/hexa-icons/<nodeType>.png`
 * (see `frontend/scripts/fetch-hexa-icons.mjs` and REV20's Deviations note in
 * `docs/specs/REV20-hexa-page.md` for which of the six types shipped with generic art — today only
 * `common_core`, since REV21 found every other type's "icon" is actually the resolved skill's own
 * art, not a per-type one — see below).
 */
export function hexaTypeIconPath(nodeType: string): string {
  return `/hexa-icons/${nodeType.replace(/_/g, '-')}.png`;
}

/**
 * REV21 (docs/specs/REV21-hexa-skill-icons.md) — a resolved HEXA skill's own icon, sourced from
 * `frontend/public/hexa-icons/<skillSlug>.png` (vendored by the same fetch script, extended to
 * pull every `hexa_class_skill` row's icon — see research 11 for the extraction method).
 */
export function hexaSkillIconPath(skillSlug: string): string {
  return `/hexa-icons/${skillSlug}.png`;
}

/**
 * Renders a node's icon: the resolved skill's own art when `skillSlug` is given (every node type
 * except `common_core`, once the character's class/pick resolves one), falling back to the node
 * type's generic icon otherwise (`common_core`, or any node whose skill hasn't resolved yet).
 * Renders nothing (not a broken-image glyph, not a letter tile) on a 404 or missing file — same
 * `LinkSkillIcon`-style contract: the node's own label is always adjacent text, so an icon-less row
 * loses nothing but a little color. Decorative (`alt=""`/`aria-hidden`).
 */
export function HexaTypeIcon({ nodeType, skillSlug }: { nodeType: string; skillSlug?: string | null }) {
  const [failed, setFailed] = useState(false);
  const src = skillSlug ? hexaSkillIconPath(skillSlug) : hexaTypeIconPath(nodeType);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (failed) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      width={20}
      height={20}
      className="size-5 shrink-0 object-contain"
      onError={() => setFailed(true)}
    />
  );
}
